import { AxiosResponse } from 'axios';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import {
  CalibrationRecord,
  CalibrationReminder,
  CalibrationUploadResult
} from '../constants/telosair-interfaces';

/**
 * Sensors that are due for calibration.
 * @param includeSnoozed - Also return reminders someone has dismissed.
 * @returns The due reminders, soonest-due first.
 */
export const getCalibrationReminders = (
  includeSnoozed = false
): Promise<AxiosResponse<CalibrationReminder[]>> => {
  return axiosInstance.get(
    `${DEV_NEW_DOMAIN_URL}/calibration/reminders?includeSnoozed=${includeSnoozed}`
  );
};

/**
 * Dismisses a sensor's calibration reminder for three months, for everyone on
 * the account.
 * @param serialNumber - Sensor to dismiss the reminder for.
 * @returns The date the reminder comes back.
 */
export const snoozeCalibrationReminder = (
  serialNumber: number
): Promise<AxiosResponse<{ snoozedUntil: string }>> => {
  return axiosInstance.post(
    `${DEV_NEW_DOMAIN_URL}/calibration/reminders/${serialNumber}/snooze`
  );
};

/**
 * Uploads a calibration CSV. Rows that cannot be read are skipped and reported
 * rather than failing the whole file.
 * @param file - CSV file to upload.
 * @returns How many coefficients were stored, and what was skipped.
 */
export const uploadCalibrationFile = (
  file: File
): Promise<AxiosResponse<CalibrationUploadResult>> => {
  const formData = new FormData();
  formData.append('file', file);
  return axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/calibration/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * Calibration coefficients on record for the account.
 * @returns The coefficients, newest effective date first.
 */
export const getCalibrations = (): Promise<AxiosResponse<CalibrationRecord[]>> => {
  return axiosInstance.get(`${DEV_NEW_DOMAIN_URL}/calibration`);
};

/**
 * Removes a calibration coefficient. Readings stop being corrected by it
 * immediately; nothing already stored is rewritten.
 * @param id - Coefficient to remove.
 * @returns Nothing.
 */
export const deleteCalibration = (id: number): Promise<AxiosResponse<void>> => {
  return axiosInstance.delete(`${DEV_NEW_DOMAIN_URL}/calibration/${id}`);
};
